import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, Input, Spin, Typography, theme } from 'antd';
import { message } from '../services/toastMessage';
import { AnimatePresence, motion } from 'framer-motion';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowLeft,
  IconArrowUp,
  IconCircleCheckFilled,
  IconClock,
  IconFlag,
  IconInfoCircle,
  IconMapPin,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getConversationMessages,
  sendConversationMessage,
} from '../services/ConversationService';
import { joinConversation, leaveConversation, onReceiveMessage, onUserTyping, sendTyping } from '../services/RealtimeService';
import { IConversationMessage, IConversationSummary } from '../interfaces/heavyDutyMachinery/IConversation';
import { OPEN_SERVICE_REQUEST_STATUSES } from '../constants/serviceRequestOptions';
import { IBooking, BookingStatus } from '../interfaces/heavyDutyMachinery/IBooking';
import { BOOKING_STATUS_LABEL_KEYS, CHAT_ENABLED_STATUSES, NEGATIVE_BOOKING_STATUSES, POSITIVE_BOOKING_STATUSES } from '../constants/bookingStatusOptions';
import { useAuth } from '../contextProviders/AuthProvider';
import { CORAL, TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';
import { tealAccentTheme } from '../theme/appTheme';
import { CONTACT_INFO_MASK_PLACEHOLDER } from '../constants/moderation';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { canAcceptQuote, canReviseQuote, isAwaitingOtherParty } from '../helpers/quoteHelper';
import ConfirmBottomSheet from './ConfirmBottomSheet';
import QuoteBreakdownSheet from './QuoteBreakdownSheet';
import QuoteCountdown from './QuoteCountdown';
import ReportModal from './ReportModal';

const { Text } = Typography;
const { TextArea } = Input;

interface MessageThreadPanelProps {
  /**
   * The thread itself, passed in rather than looked up here.
   *
   * It used to be fetched from the booking's id, which quietly made a booking
   * mandatory for a chat to exist -- and a quote-request thread has no
   * booking, so opening one span forever on a spinner. The caller already has
   * this object; handing it over is both simpler and the thing that makes the
   * second kind of conversation possible.
   */
  conversation: IConversationSummary;
  /**
   * Undefined on a quote-request thread: there is no booking yet, which is
   * the whole point of a quote request. Everything about dates, money and
   * booking status below is gated on this.
   */
  booking?: IBooking;
  detailsPanelOpen: boolean;
  onToggleDetailsPanel: () => void;
  onViewAddress: () => void;
  canCancel: boolean;
  cancelling: boolean;
  onCancel: () => void;
  // Reserva diferida -- only ever relevant while booking.status is
  // AwaitingQuote/AwaitingPayment. onAcceptQuote is undefined once there's
  // nothing left to accept (AwaitingPayment).
  //
  // Accepting is offered to whichever party didn't put the live amount on
  // the table (see quoteHelper); this panel gates it, callers don't have to.
  // onCancelQuote is the guest's walk-away; onProposeQuote/onRejectQuote are
  // the host's, so a caller passes one pair or the other by role rather than
  // this component guessing.
  onAcceptQuote?: () => void;
  acceptingQuote?: boolean;
  onCancelQuote?: () => void;
  cancellingQuote?: boolean;
  onProposeQuote?: () => void;
  onRejectQuote?: () => void;
  /**
   * Host-only, AwaitingPayment: the only thing left they can actually DO.
   * Once a price is accepted the host can no longer counter or reject it
   * (see quoteHelper), and the guest's own "cancel request" is refused for
   * them server-side -- so without this their side of an accepted quote is
   * a card with nothing on it but a countdown they cannot affect.
   */
  onHostCancelBooking?: () => void;
  /** Guest-only, AwaitingPayment: opens the real payment step (see Messages' own ResumePaymentModal). */
  onPayNow?: () => void;
  /**
   * Quote-request threads only: takes whoever is reading back to the request
   * itself, which is where quoting, accepting and withdrawing live. This panel
   * deliberately offers none of those -- a negotiation has one place.
   */
  onOpenServiceRequest?: () => void;
  // Set only on mobile (see Messages.tsx), where the thread is its own
  // full-screen "screen" navigated to from the conversation list rather
  // than a permanent column -- swaps the header's back arrow for one that
  // returns to that list instead of leaving the page entirely. On
  // desktop/tablet there's nothing to go "back" to (the list is a
  // permanent column), so no arrow is rendered there at all.
  onBack?: () => void;
  // Tablet/mobile show the booking-details toggle as a header icon (the
  // panel opens as a Drawer for them, see Messages.tsx) instead of the
  // desktop-only inline text button further down in the action bar.
  showHeaderDetailsToggle?: boolean;
  // Fired once the initial page of messages for this conversation has
  // loaded -- the same GET call the backend already uses to mark every
  // unread message from the peer as Read (see ConversationsController.
  // GetMessages's own doc comment: that happens for the whole conversation,
  // not just the returned page), so by the time this fires the badge really
  // is stale server-side too. Lets Messages.tsx zero out this conversation's
  // own unreadCount in its list state immediately, instead of that count
  // only catching up on the next full reload.
  onMessagesRead?: (conversationId: string) => void;
}

// Confirmed/Completed use the filled variant (a solid disc, not just an
// outlined glyph) -- see statusCardStyle below, same "solid badge" look as
// the app's other positive-state confirmations rather than a bare colored
// line icon.
const STATUS_CARD_ICON: Record<BookingStatus, React.ReactNode> = {
  Pending: <IconClock size={18} />,
  Confirmed: <IconCircleCheckFilled size={20} />,
  Completed: <IconCircleCheckFilled size={20} />,
  Cancelled: <IconX size={18} />,
  HostNoShow: <IconAlertTriangle size={18} />,
  GuestNoShow: <IconAlertTriangle size={18} />,
  AwaitingQuote: <IconClock size={18} />,
  AwaitingPayment: <IconClock size={18} />,
};

const AUTO_MESSAGE_KEYS: Record<BookingStatus, string> = {
  Pending: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_PENDING,
  Confirmed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_CONFIRMED,
  Completed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_COMPLETED,
  Cancelled: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_CANCELLED,
  HostNoShow: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_NO_SHOW,
  GuestNoShow: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_NO_SHOW,
  AwaitingQuote: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_AWAITING_QUOTE,
  AwaitingPayment: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_AUTO_MSG_AWAITING_PAYMENT,
};

// The auto message bubble and the typing indicator are plain system notices
// with no status of their own to reflect -- they keep this one neutral
// treatment. The booking status card below has its own statusCardStyle
// instead, now that the app's color pass has landed elsewhere (teal for
// confirmations/positive states, same criterion as the "verified" badges
// on HeavyDutyMachineryDetail).
const systemBlockStyle = (token: ReturnType<typeof theme.useToken>['token']): React.CSSProperties => ({
  background: token.colorBgContainer,
  border: `1px solid ${token.colorBorder}`,
  borderRadius: 16,
  color: token.colorText,
});

// Confirmed/Completed both read as "this booking is on/done and fine" --
// the same positive-state signal the rest of the app already gives its own
// teal accent (AvailabilityCalendar's selected day, HourlyRangeTimeline's
// confirmed range). Cancelled/HostNoShow/GuestNoShow read as "this didn't
// happen" -- antd's own colorError/colorErrorBg semantic tokens, the same
// danger tone the conversation list's own status badge already uses (see
// Messages.tsx), so the two surfaces read as one consistent status system
// instead of only one of them being color-coded. Pending is the one status
// left with no established color of its own -- still the plain neutral
// card. POSITIVE_BOOKING_STATUSES/NEGATIVE_BOOKING_STATUSES live in
// bookingStatusOptions.ts (shared with Messages.tsx) rather than as local
// copies here, now that both this card and that badge need the same split.
const statusCardStyle = (status: BookingStatus, token: ReturnType<typeof theme.useToken>['token']): React.CSSProperties => {
  if (POSITIVE_BOOKING_STATUSES.includes(status)) return { background: TEAL_SOFT_BG, border: `1px solid ${TEAL}`, borderRadius: 16, color: TEAL };
  if (NEGATIVE_BOOKING_STATUSES.includes(status))
    return { background: token.colorErrorBg, border: `1px solid ${token.colorError}`, borderRadius: 16, color: token.colorError };
  return systemBlockStyle(token);
};

const pillButtonStyle = (token: ReturnType<typeof theme.useToken>['token']): React.CSSProperties => ({
  borderRadius: 999,
  border: `1px solid ${token.colorBorder}`,
  background: 'transparent',
});

// "Cancelar reserva" is the one potentially destructive action in this bar
// (unlike "Ver dirección"/"Ver detalles", which are purely informational) --
// borrows the app's existing warning semantic (see HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES'
// own warning tone) instead of the neutral pill every button used to share,
// so it reads as "think before you tap" without going as far as an outright
// error/red.
const warningPillButtonStyle = (token: ReturnType<typeof theme.useToken>['token']): React.CSSProperties => ({
  borderRadius: 999,
  border: `1px solid ${token.colorWarning}`,
  background: token.colorWarningBg,
  color: token.colorWarning,
});

// How close to the top of the loaded messages (px) triggers fetching the
// previous (older) page -- far enough that the fetch is usually done before
// the user's eye actually reaches the current oldest message.
const LOAD_OLDER_THRESHOLD_PX = 150;
// How close to the bottom (px) counts as "already following the
// conversation" -- within this, new messages/typing still auto-scroll like
// before; further up, they don't (see the "new message" pill instead).
const NEAR_BOTTOM_THRESHOLD_PX = 80;

// No "stopped typing" event exists server-side (HeavyDutyMachineryHub only ever
// pushes "UserTyping", see its own doc comment) -- so the indicator just
// clears itself if no fresh signal arrives within this window, same
// pattern most chat UIs use instead of a matching stop event.
const TYPING_INDICATOR_TIMEOUT_MS = 3000;
// How often we're willing to re-announce "I'm typing" to the peer while
// they keep typing, so every keystroke doesn't hit the hub.
const TYPING_SEND_THROTTLE_MS = 2000;

// The actual check-in/check-out of THIS booking, date and time both --
// deliberately not the abbreviated "18–18 Aug" summary used elsewhere
// (e.g. the conversation list), since here it's the one place a host/guest
// checks exactly when to show up.
const dateTimeRangeLabel = (booking: IBooking) => {
  const fmt = (d: dayjs.Dayjs) => d.format('D MMM, HH:mm');
  return `${fmt(dayjs(booking.startAt))} – ${fmt(dayjs(booking.endAt))}`;
};

const TypingDots: React.FC = () => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '2px 2px' }}>
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }}
      />
    ))}
  </div>
);

/**
 * Center panel of the redesigned /messages page: the real-time Conversation
 * thread for a Booking, with a pinned status card + a one-off client-side
 * "system" bubble (never persisted -- the backend doesn't seed a first
 * Message on Conversation creation) above the actual messages, and a quick
 * actions bar. "Cancelar reserva" here calls the SAME onCancel the details
 * panel's button calls -- Messages.tsx owns the one cancelBooking call.
 */
/**
 * How the quote card changes its mind.
 *
 * The booking behind this thread now moves under the reader's feet (see
 * Messages' own live-refresh effect): the host accepts, and the guest's
 * "ver presupuesto" has to become "pagar ahora" while they are looking at
 * it. Swapping the block outright makes the buttons appear to teleport, and
 * a payment button that materialises where a different one just was is the
 * worst possible thing to mis-click.
 *
 * So the outgoing state leaves before the incoming one arrives (mode="wait"),
 * on the same "fade up into place" curve the rest of the app enters on --
 * see IncomingBookings' NEW_BOOKING_ENTRY_ANIMATION. Slightly quicker,
 * because this is a change inside a card the reader is already looking at
 * rather than a new card arriving in a list.
 */
const QUOTE_STATE_TRANSITION = {
  initial: { opacity: 0, y: -4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 4 },
  transition: { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] as const },
};

const MessageThreadPanel: React.FC<MessageThreadPanelProps> = ({
  conversation,
  booking,
  detailsPanelOpen,
  onToggleDetailsPanel,
  onViewAddress,
  canCancel,
  cancelling,
  onCancel,
  onAcceptQuote,
  acceptingQuote,
  onCancelQuote,
  cancellingQuote,
  onProposeQuote,
  onRejectQuote,
  onHostCancelBooking,
  onPayNow,
  onOpenServiceRequest,
  onBack,
  showHeaderDetailsToggle,
  onMessagesRead,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { userId } = useAuth();
  const [messages, setMessages] = useState<IConversationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  // Collapses the header (hides the date subtitle, shrinks the status card
  // to one compact line) once the messages body is scrolled away from the
  // very top -- purely a `scrollTop` read on the body's own scroll event,
  // nothing to do with the page/window scroll (there isn't one, see point
  // 2 of this redesign pass: this panel's height is fully bounded by its
  // flex parent, only the body below scrolls).
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [cancelQuoteSheetOpen, setCancelQuoteSheetOpen] = useState(false);
  const [quoteSheetOpen, setQuoteSheetOpen] = useState(false);
  const [reportMessageId, setReportMessageId] = useState<string | undefined>(undefined);
  // Whether there's an older page of messages beyond what's loaded --
  // gates both the scroll-up-to-load-more trigger and the synthetic
  // "conversation started" system bubble below, which must only show once
  // this is false (otherwise it'd claim to be the start of the thread
  // while older real messages still exist above it).
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Shown instead of auto-scrolling when a message arrives while the user
  // has scrolled up into history -- see the effects below.
  const [showNewMessagePill, setShowNewMessagePill] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastTypingSentAtRef = useRef(0);
  const peerTypingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Set right before prepending an older page (see loadOlder), read and
  // cleared by the layout effect right after -- that's what tells "messages
  // just changed because we loaded older history" apart from every other
  // reason messages can change (new arrival, initial load), which need a
  // scroll-to-bottom instead of a position restore, handled at their own
  // call sites.
  const pendingOlderPrependRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(null);

  // Whether the signed-in user is this booking's guest or its host --
  // drives both the peer's role label ("(anfitrión)"/"(huésped)") and,
  // indirectly, which of the two names in the pair is "Tú".
  // Only ever read inside blocks that already require a booking (the quote
  // cards, the payment row). A quote-request thread carries no role either
  // way -- see peerLabel, which drops the role rather than guessing it.
  const isGuest = !!booking && booking.guestUserId === userId;
  // See quoteHelper: you can only accept a price the OTHER side proposed.
  const awaitingOtherParty = !!booking && isAwaitingOtherParty(booking, userId ?? undefined);
  const isPositiveStatus = !!booking && POSITIVE_BOOKING_STATUSES.includes(booking.status);
  // Only for the status card's own accent color below.
  const statusAccentColor = isPositiveStatus ? TEAL : booking && NEGATIVE_BOOKING_STATUSES.includes(booking.status) ? token.colorError : undefined;
  // Every gate on *messaging* (handleSend, the input's disabled state) keys
  // on this instead of isPositiveStatus -- AwaitingQuote/AwaitingPayment
  // (reserva diferida) need the chat open for the price negotiation, but
  // aren't a "positive" outcome the way Confirmed/Completed are (see the
  // backend's own ConversationsController.SendMessage gate, which allows
  // the exact same four statuses).
  // A quote-request thread is open while the negotiation is: sent, quoted or
  // accepted. Once it is declined, expired or withdrawn there is nothing left
  // to say -- the same gate ConversationsController applies server-side, so
  // the box being enabled here always means a message will actually send.
  const isChatEnabled = booking
    ? CHAT_ENABLED_STATUSES.includes(booking.status)
    : OPEN_SERVICE_REQUEST_STATUSES.some((status) => status === conversation.serviceRequestStatus);
  const peerRoleLabel = translate(t, isGuest ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOST_LABEL : TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_GUEST_LABEL);
  // The role suffix needs a booking to be true. On a quote-request thread the
  // name alone is both honest and enough: you know perfectly well whether you
  // are the one with the leak or the one fixing it.
  const peerLabel = booking
    ? conversation.peerDisplayName
      ? `${conversation.peerDisplayName} (${peerRoleLabel})`
      : peerRoleLabel
    : conversation.peerDisplayName || peerRoleLabel;

  // True when the messages body is scrolled at (or within
  // NEAR_BOTTOM_THRESHOLD_PX of) its own bottom edge -- i.e. the user is
  // still "following" the live conversation rather than reading up into
  // history. Read synchronously (never stale, no dependency array) right
  // before a message/typing event is about to change the content, since
  // that's the only moment it matters.
  const isNearBottom = () => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD_PX;
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
  };

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setHasMoreOlder(true);
    setShowNewMessagePill(false);
    getConversationMessages(conversation.id)
      .then((page) => {
        setMessages(page.items);
        setHasMoreOlder(page.hasMore);
        // Opening a thread always lands on the latest messages, scrolled to
        // the very bottom -- unconditional, unlike every other scroll
        // decision below (which check whether the user was already there).
        scrollToBottom();
        // The same GET call above is what marks every unread message from
        // the peer as Read server-side -- see the prop's own doc comment.
        onMessagesRead?.(conversation.id);
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_LOAD_ERROR)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  // Restores the user's exact scroll anchor after an older page gets
  // prepended (see loadOlder) -- runs synchronously after the DOM reflects
  // the taller content but before the browser paints, so there's no visible
  // jump/flash. Standard "measure the height that was just added above you,
  // shift scrollTop by exactly that much" technique. A no-op for every
  // OTHER reason `messages` changes (new arrival, initial load), since
  // pendingOlderPrependRef is only ever set right before an older-page
  // prepend.
  useLayoutEffect(() => {
    const pending = pendingOlderPrependRef.current;
    if (!pending) return;
    pendingOlderPrependRef.current = null;
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTop = pending.prevScrollTop + (el.scrollHeight - pending.prevScrollHeight);
  }, [messages]);

  // Fetches the page of messages just before the oldest one currently
  // loaded -- triggered by scrolling near the top (see
  // handleMessagesScroll). Guarded on loadingOlder/hasMoreOlder so a fast
  // scroll or a scroll that stays near the top doesn't queue up duplicate
  // requests once one's already in flight or there's nothing left to fetch.
  const loadOlder = async () => {
    if (loadingOlder || !hasMoreOlder || !conversation || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const cursor = Number(messages[0].id);
      const page = await getConversationMessages(conversation.id, cursor);
      const el = scrollContainerRef.current;
      if (el) pendingOlderPrependRef.current = { prevScrollHeight: el.scrollHeight, prevScrollTop: el.scrollTop };
      setMessages((prev) => [...page.items, ...prev]);
      setHasMoreOlder(page.hasMore);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_LOAD_ERROR));
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setHeaderCollapsed(el.scrollTop > 8);
    if (el.scrollTop < LOAD_OLDER_THRESHOLD_PX) loadOlder();
  };

  useEffect(() => {
    if (!conversation) return undefined;
    return onReceiveMessage((incoming) => {
      if (incoming.conversationId !== conversation.id) return;
      // Read BEFORE appending -- scrollHeight is about to grow, so this has
      // to reflect where the user was a moment ago, not where "at the
      // bottom" now sits after the new message's own height is added in.
      const wasNearBottom = isNearBottom();
      setMessages((prev) => [...prev, incoming]);
      if (incoming.senderId === conversation.peerUserId) setPeerTyping(false);
      if (wasNearBottom) scrollToBottom();
      else setShowNewMessagePill(true);
    });
  }, [conversation]);

  useEffect(() => {
    if (!conversation) return undefined;
    return onUserTyping((fromUserId) => {
      if (fromUserId !== conversation.peerUserId) return;
      const wasNearBottom = isNearBottom();
      setPeerTyping(true);
      if (wasNearBottom) scrollToBottom();
      clearTimeout(peerTypingTimeoutRef.current);
      peerTypingTimeoutRef.current = setTimeout(() => setPeerTyping(false), TYPING_INDICATOR_TIMEOUT_MS);
    });
  }, [conversation]);

  useEffect(() => () => clearTimeout(peerTypingTimeoutRef.current), []);

  // Tells the backend "I have this conversation open and visible" so a
  // message someone sends into it right now doesn't also ring their bell
  // notification (see ConversationsController.SendMessage's
  // IActiveConversationTracker check) -- re-armed on every conversation
  // switch and on regaining tab visibility, and explicitly released the
  // moment either goes away, since a backgrounded/hidden tab should still
  // get notified same as if the thread were closed entirely.
  useEffect(() => {
    if (!conversation) return undefined;
    const conversationId = conversation.id;
    const syncActiveState = () => {
      if (document.visibilityState === 'visible') joinConversation(conversationId);
      else leaveConversation();
    };
    syncActiveState();
    document.addEventListener('visibilitychange', syncActiveState);
    return () => {
      document.removeEventListener('visibilitychange', syncActiveState);
      leaveConversation();
    };
  }, [conversation]);

  const handleTextChange = (value: string) => {
    setText(value);
    if (!conversation || !value.trim()) return;
    const now = Date.now();
    if (now - lastTypingSentAtRef.current < TYPING_SEND_THROTTLE_MS) return;
    lastTypingSentAtRef.current = now;
    sendTyping(conversation.peerUserId);
  };

  const handleSend = async () => {
    if (!conversation || !text.trim() || !isChatEnabled) return;
    setSending(true);
    try {
      const sent = await sendConversationMessage(conversation.id, text.trim());
      setMessages((prev) => [...prev, sent]);
      setText('');
      setShowNewMessagePill(false);
      scrollToBottom();
    } catch (error: any) {
      // BOOKING_CHAT_NOT_AVAILABLE is a machine-readable code (see
      // ConversationsController.SendMessage), not a human sentence -- same
      // convention as AuthController's EMAIL_NOT_CONFIRMED. Covers the race
      // where the booking's status changes server-side while this panel is
      // already open with text typed in (isChatEnabled above was still
      // true when the user started typing). Any other error still falls
      // back to the generic translated message rather than a raw string.
      const translatedMessage =
        error.message === 'BOOKING_CHAT_NOT_AVAILABLE'
          ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_NOT_AVAILABLE)
          : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_SEND_ERROR);
      message.error(translatedMessage);
    } finally {
      setSending(false);
    }
  };

  const senderLabel = (senderId: string) => (senderId === userId ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_YOU_LABEL) : peerLabel);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
      <div style={{ flexShrink: 0, padding: '12px 16px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: showHeaderDetailsToggle ? 'space-between' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: showHeaderDetailsToggle ? 0 : undefined, flex: showHeaderDetailsToggle ? 1 : undefined }}>
            {onBack && (
              <Button
                type="text"
                size="small"
                icon={<IconArrowLeft size={20} />}
                aria-label={translate(t, TRANSLATIONS.BACK)}
                onClick={onBack}
              />
            )}
            <Text
              strong
              style={{
                fontSize: 16,
                ...(showHeaderDetailsToggle ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
              }}
            >
              {conversation.heavyDutyMachineryTitle}
            </Text>
          </div>
          {showHeaderDetailsToggle && (
            <Button
              type="text"
              size="small"
              icon={<IconInfoCircle size={20} />}
              aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_VIEW_DETAILS_BUTTON)}
              onClick={onToggleDetailsPanel}
            />
          )}
        </div>
        <div
          style={{
            maxHeight: headerCollapsed ? 0 : 20,
            opacity: headerCollapsed ? 0 : 1,
            overflow: 'hidden',
            transition: 'max-height 220ms ease, opacity 220ms ease',
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {booking
              ? `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_BOOKING_PREFIX)}: ${conversation.heavyDutyMachineryTitle} · ${dateTimeRangeLabel(booking)}`
              : translate(t, TRANSLATIONS.MESSAGES_QUOTE_REQUEST_SUBTITLE)}
          </Text>
        </div>
      </div>

      {/* A quote-request thread has no booking, no dates and no money yet --
          what it has is a negotiation, and the place to act on it is the
          request itself. So the pinned card here is one line and a way back
          to it, rather than a booking card with nothing in it. */}
      {!booking && (
        <div style={{ flexShrink: 0, padding: headerCollapsed ? '6px 16px' : '10px 16px', transition: 'padding 220ms ease' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              padding: '10px 14px',
              borderRadius: 14,
              background: TEAL_SOFT_BG,
            }}
          >
            <Text strong style={{ fontSize: 13, color: TEAL }}>
              {translate(t, TRANSLATIONS.MESSAGES_QUOTE_REQUEST_CARD_TITLE)}
            </Text>
            {conversation.serviceRequestId && onOpenServiceRequest && (
              <Button size="small" shape="round" style={{ marginLeft: 'auto' }} onClick={onOpenServiceRequest}>
                {translate(t, TRANSLATIONS.MESSAGES_QUOTE_REQUEST_OPEN)}
              </Button>
            )}
          </div>
        </div>
      )}

      {booking && (
      <div style={{ flexShrink: 0, padding: headerCollapsed ? '6px 16px' : '10px 16px', transition: 'padding 220ms ease' }}>
        <div
          style={{
            ...statusCardStyle(booking.status, token),
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: headerCollapsed ? '6px 14px' : '10px 14px',
            transition: 'padding 220ms ease',
          }}
        >
          <span style={{ display: 'flex', color: statusAccentColor ?? token.colorTextSecondary }}>{STATUS_CARD_ICON[booking.status]}</span>
          <div>
            <Text strong style={{ display: 'block', color: statusAccentColor }}>
              {booking.status === 'HostNoShow' || booking.status === 'GuestNoShow'
                ? translate(t, BOOKING_STATUS_LABEL_KEYS[booking.status])
                : `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_BOOKING_PREFIX)} ${translate(t, BOOKING_STATUS_LABEL_KEYS[booking.status]).toLowerCase()}`}
            </Text>
            <div
              style={{
                maxHeight: headerCollapsed ? 0 : 20,
                opacity: headerCollapsed ? 0 : 1,
                overflow: 'hidden',
                transition: 'max-height 220ms ease, opacity 220ms ease',
              }}
            >
              <Text type="secondary" style={{ fontSize: 13, color: statusAccentColor }}>
                {dateTimeRangeLabel(booking)} · {(booking.quoteCurrentAmount ?? booking.totalPrice).toFixed(2)}€
              </Text>
            </div>
          </div>
        </div>

        {/* Reserva diferida. Shown to BOTH parties: the host used to have to
            leave the conversation and go to IncomingBookings to see or decide
            anything, which is backwards -- the negotiation happens in this
            thread, so the quote has to be reachable from it.

            The card itself never carries a price decision. It says where the
            negotiation stands and opens the breakdown; accepting, countering
            and rejecting all live inside that sheet, so nobody commits to a
            number without having had it in front of them. The guest's
            "cancel request" stays here because it isn't a decision about the
            price, it's walking away from the whole thing.

            Kept outside the price line's own headerCollapsed animation above
            -- an actionable button shouldn't disappear just because someone
            scrolled down into the thread. */}
        <AnimatePresence mode="wait" initial={false}>
        {booking.status === 'AwaitingQuote' && (
          <motion.div key="quote-awaiting" style={{ marginTop: 10 }} {...QUOTE_STATE_TRANSITION}>
            {awaitingOtherParty ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Text strong style={{ fontSize: 13 }}>
                  {translate(t, isGuest ? TRANSLATIONS.QUOTE_AWAITING_HOST_TITLE : TRANSLATIONS.QUOTE_AWAITING_GUEST_TITLE)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {translate(t, isGuest ? TRANSLATIONS.QUOTE_AWAITING_HOST_DESCRIPTION : TRANSLATIONS.QUOTE_AWAITING_GUEST_DESCRIPTION)}
                </Text>
                {/* Only ever set while the host still owes their first reply
                    (BookingController.ProposeQuoteAmount clears it), so this
                    is always "how long the host has left" and never a clock
                    running against the guest. The window is whatever the host
                    configured, so it can't be stated as a constant in copy. */}
                {booking.quoteRespondByUtc && (
                  <QuoteCountdown deadlineUtc={booking.quoteRespondByUtc} remainingSeconds={booking.quoteRespondInSeconds} />
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Text strong style={{ fontSize: 13 }}>
                  {translate(t, TRANSLATIONS.QUOTE_YOUR_TURN_TITLE)}
                </Text>
                {/* The host's own reason for the new amount -- the single most
                    useful line on this card when there is one. */}
                {booking.quoteReason && <Text style={{ fontSize: 13 }}>{booking.quoteReason}</Text>}
                {/* The deadline changes hands with the counter-offer, so on
                    your own turn it's counting against YOU. Showing it is the
                    difference between deciding in time and finding out the
                    request was cancelled. */}
                {booking.quoteRespondByUtc && (
                  <QuoteCountdown deadlineUtc={booking.quoteRespondByUtc} remainingSeconds={booking.quoteRespondInSeconds} variant="yours" />
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {/* Teal, not the brand coral: this is money taking shape, the
                  same accent the checkout summary and the calendar speak. */}
              <ConfigProvider theme={tealAccentTheme}>
                <Button size="small" type="primary" onClick={() => setQuoteSheetOpen(true)}>
                  {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_VIEW_BUTTON)}
                </Button>
              </ConfigProvider>
              {onCancelQuote && (
                <Button size="small" style={warningPillButtonStyle(token)} loading={cancellingQuote} onClick={() => setCancelQuoteSheetOpen(true)}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_BUTTON)}
                </Button>
              )}
            </div>
          </motion.div>
        )}
        {/* Quote agreed, money still missing. The guest was landed here by the
            "quote accepted" notification and then had nowhere to go: the card
            said "pending payment" and offered only a way to cancel. Paying is
            the whole point of this state, so it's the primary action -- and it
            opens the breakdown first, so the guest sees what the charge is
            made of before handing over a card rather than after. */}
        {/* Both roles, not just the guest. This block used to be guest-only,
            so the moment a host accepted a quote their own thread lost its
            card entirely: the negotiation they had just settled vanished from
            the screen with nothing in its place, right when they most wanted
            to know whether the money had arrived. The two sides need
            different words and different buttons, not different existence --
            one of them owes a payment, the other is holding a machine off the
            market waiting for it. */}
        {booking.status === 'AwaitingPayment' && (
          <motion.div key="quote-awaiting-payment" style={{ marginTop: 10 }} {...QUOTE_STATE_TRANSITION}>
            <Text strong style={{ fontSize: 13, display: 'block' }}>
              {translate(t, TRANSLATIONS.QUOTE_ACCEPTED_AWAITING_PAYMENT_TITLE)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
              {translate(t, isGuest
                ? TRANSLATIONS.QUOTE_ACCEPTED_AWAITING_PAYMENT_DESCRIPTION
                : TRANSLATIONS.QUOTE_ACCEPTED_AWAITING_PAYMENT_HOST_DESCRIPTION)}
            </Text>
            {/* The pay-by window is the host's own setting, so it can't be
                stated as a constant -- and a guest who doesn't know there IS
                one only finds out when the booking is already gone. The host
                reads the same deadline from the other end: it is how long
                their machine stays held. */}
            {booking.paymentRequiredByUtc && (
              <div style={{ marginTop: 6 }}>
                <QuoteCountdown
                  deadlineUtc={booking.paymentRequiredByUtc}
                  remainingSeconds={booking.paymentRequiredInSeconds}
                  variant={isGuest ? 'pay' : 'theirPay'}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {/* Paying is the guest's; reading what was agreed is both
                  sides'. The host keeps a way into the breakdown so the
                  figure they accepted stays reachable from the thread. */}
              {isGuest && onPayNow && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setQuoteSheetOpen(true)}
                  style={{ background: TEAL, borderColor: TEAL }}
                >
                  {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_PAY_BUTTON)}
                </Button>
              )}
              {!isGuest && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setQuoteSheetOpen(true)}
                  style={{ background: TEAL, borderColor: TEAL }}
                >
                  {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_VIEW_BUTTON)}
                </Button>
              )}
              {isGuest && onCancelQuote && (
                <Button size="small" style={warningPillButtonStyle(token)} loading={cancellingQuote} onClick={() => setCancelQuoteSheetOpen(true)}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_BUTTON)}
                </Button>
              )}
              {/* The host's only remaining move. It is a real cancellation
                  with a reason the guest reads, not the guest's own quiet
                  withdrawal -- a machine was held off the market on the
                  strength of a price this host agreed to, and backing out of
                  that is something you explain. */}
              {!isGuest && onHostCancelBooking && (
                <Button size="small" style={warningPillButtonStyle(token)} onClick={onHostCancelBooking}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_BUTTON)}
                </Button>
              )}
            </div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
      )}

      <div
        ref={scrollContainerRef}
        onScroll={handleMessagesScroll}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          // Safety net: bubbles below cap width via `maxWidth` + `minWidth: 0`
          // (a long unbroken run of text would otherwise refuse to shrink
          // below its own min-content width, the horizontal analogue of the
          // min-height:0 fix above, winning over maxWidth and pushing this
          // container wider than the screen -- hence why that alone showed
          // up as sideways scroll here specifically).
          overflowX: 'hidden',
          // Stops the desktop mouse-wheel "scroll latching" quirk where a
          // fast wheel gesture that hits this element's top/bottom
          // boundary keeps getting routed here (doing nothing) instead of
          // ending, until the gesture goes idle or the cursor moves
          // elsewhere -- see Messages.tsx's own identical comment on the
          // conversation list, which has the same fix.
          overscrollBehavior: 'contain',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '0 16px 16px',
        }}
      >
        {/* Shown while fetching an older page (see loadOlder/
            handleMessagesScroll) -- sits above the oldest currently-loaded
            message, at whatever's momentarily the top of the scrollable
            content. */}
        {loadingOlder && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <Spin size="small" />
          </div>
        )}

        {/* The host's own welcomeMessage already arrives as a real message
            in the list below (see BookingController.CreateBooking) -- this
            synthetic bubble only fills in for Confirmed when the host never
            set one, and unconditionally for every other status, which has
            no real-message equivalent at all. Gated on !hasMoreOlder too --
            it's meant to read as "this is where the conversation started",
            which isn't true (yet) while older real messages might still be
            waiting further up the history this panel hasn't loaded. */}
        {!hasMoreOlder && booking && (booking.status !== 'Confirmed' || !booking.welcomeMessage) && (
          <div
            style={{
              ...systemBlockStyle(token),
              alignSelf: 'flex-start',
              padding: '10px 14px',
              maxWidth: '85%',
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {translate(t, AUTO_MESSAGE_KEYS[booking.status])}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          messages.map((m) => {
            const isOwn = m.senderId === userId;
            return (
              <div key={m.id} style={{ alignSelf: isOwn ? 'flex-end' : 'flex-start', maxWidth: '75%', minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2, textAlign: isOwn ? 'right' : 'left' }}>
                  {senderLabel(m.senderId)}
                </Text>
                <div
                  style={{
                    // Own messages: solid CORAL (this app's brand/primary-action
                    // color, same criterion as every other primary CTA) instead
                    // of the plain near-black colorText every bubble used to
                    // share -- the peer's bubble stays on the neutral fill,
                    // unchanged.
                    background: isOwn ? CORAL : token.colorFillTertiary,
                    color: isOwn ? '#fff' : token.colorText,
                    borderRadius: 18,
                    padding: '8px 14px',
                    overflowWrap: 'anywhere',
                  }}
                >
                  <div>{m.text}</div>
                  <Text style={{ fontSize: 10, opacity: 0.7, color: isOwn ? '#fff' : token.colorTextSecondary }}>
                    {dayjs(m.sentAt).format('HH:mm')}
                  </Text>
                </div>
                {/* Server-side masking (ConversationsController.SendMessage)
                    already stripped the actual contact info before this ever
                    reached us -- this is just a discreet "why does part of
                    this message look like that" note, not a warning, so it
                    stays tiny and low-contrast like the report button below
                    rather than an alert. */}
                {m.text.includes(CONTACT_INFO_MASK_PLACEHOLDER) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                    <IconInfoCircle size={11} color={token.colorTextTertiary} />
                    <Text style={{ fontSize: 10, color: token.colorTextTertiary }}>
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_MASKED_CONTENT_NOTICE)}
                    </Text>
                  </div>
                )}
                {/* Never on your own messages -- reporting yourself makes no
                    sense. Icon-only, small and low-contrast: this sits right
                    under every single peer bubble, so anything louder would
                    compete with the actual conversation for attention. */}
                {!isOwn && (
                  <button
                    type="button"
                    aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_REPORT_BUTTON_LABEL)}
                    onClick={() => setReportMessageId(m.id)}
                    style={{ display: 'flex', border: 'none', background: 'none', padding: '2px 0', margin: 0, cursor: 'pointer', color: token.colorTextTertiary }}
                  >
                    <IconFlag size={12} />
                  </button>
                )}
              </div>
            );
          })
        )}

        {peerTyping && (
          <div style={{ alignSelf: 'flex-start', maxWidth: '75%', minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
              {peerLabel}
            </Text>
            <div
              style={{
                background: token.colorFillTertiary,
                color: token.colorTextSecondary,
                borderRadius: 18,
                padding: '8px 14px',
                display: 'inline-flex',
              }}
            >
              <Text type="secondary" style={{ fontSize: 12, marginRight: 6 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_TYPING_INDICATOR)}
              </Text>
              <TypingDots />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Shown instead of auto-scrolling (see onReceiveMessage above) when a
          message arrives while the user has scrolled up into history --
          floats over the quick-actions bar/input below rather than inside
          the scrollable messages area itself, so it stays put regardless of
          scroll position. */}
      <AnimatePresence>
        {showNewMessagePill && (
          <motion.button
            key="new-message-pill"
            type="button"
            onClick={() => {
              scrollToBottom();
              setShowNewMessagePill(false);
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              bottom: 118,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              borderRadius: 999,
              border: 'none',
              background: CORAL,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(0,0,0,0.24)',
              cursor: 'pointer',
            }}
          >
            <IconArrowDown size={14} />
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_NEW_MESSAGE_PILL)}
          </motion.button>
        )}
      </AnimatePresence>

      <div style={{ flexShrink: 0, display: 'flex', gap: 8, padding: '10px 16px', borderTop: `1px solid ${token.colorBorderSecondary}`, flexWrap: 'wrap' }}>
        <Button size="small" icon={<IconMapPin size={14} />} style={pillButtonStyle(token)} onClick={onViewAddress}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_VIEW_ADDRESS_BUTTON)}
        </Button>
        {!showHeaderDetailsToggle && !detailsPanelOpen && (
          <Button size="small" icon={<IconInfoCircle size={14} />} style={pillButtonStyle(token)} onClick={onToggleDetailsPanel}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_VIEW_DETAILS_BUTTON)}
          </Button>
        )}
        {canCancel && (
          <Button
            size="small"
            icon={<IconX size={14} />}
            loading={cancelling}
            style={warningPillButtonStyle(token)}
            onClick={() => setCancelSheetOpen(true)}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_BUTTON)}
          </Button>
        )}
      </div>

      {/* Every price decision lives in here rather than on the card above,
          for both roles: nobody should commit to a number they haven't had
          broken down in front of them. Accept is offered only to whoever
          didn't put the live amount on the table (quoteHelper). */}
      {booking && (
      <QuoteBreakdownSheet
        open={quoteSheetOpen}
        booking={booking}
        userId={userId ?? undefined}
        onClose={() => setQuoteSheetOpen(false)}
        accepting={acceptingQuote}
        onAccept={
          onAcceptQuote && canAcceptQuote(booking, userId ?? undefined)
            ? () => {
                setQuoteSheetOpen(false);
                onAcceptQuote();
              }
            : undefined
        }
        onPropose={
          // canReviseQuote, not just "is there a callback": a host who has
          // already accepted kept being offered a counter-offer the server
          // then refused. See quoteHelper.
          onProposeQuote && canReviseQuote(booking)
            ? () => {
                setQuoteSheetOpen(false);
                onProposeQuote();
              }
            : undefined
        }
        onReject={
          onRejectQuote && canReviseQuote(booking)
            ? () => {
                setQuoteSheetOpen(false);
                onRejectQuote();
              }
            : undefined
        }
        onCancel={
          // The guest's own way out, so their side of the sheet is never
          // actionless while they wait on the host -- same reasoning as
          // MyBookings' own copy of this. Routed through the same
          // confirmation the card's own button opens, not straight to
          // onCancelQuote: cancelling is cancelling, wherever it is pressed.
          onCancelQuote
            ? () => {
                setQuoteSheetOpen(false);
                setCancelQuoteSheetOpen(true);
              }
            : undefined
        }
        onPay={
          onPayNow && booking.status === 'AwaitingPayment'
            ? () => {
                setQuoteSheetOpen(false);
                onPayNow();
              }
            : undefined
        }
      />
      )}

      {/* Cancelling a booking, which a quote-request thread has none of. A
          request is withdrawn from the request itself, not from here. */}
      {booking && (
        <>
      <ConfirmBottomSheet
        open={cancelSheetOpen}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_CONFIRM)}
        description={`${conversation.heavyDutyMachineryTitle} · ${dateTimeRangeLabel(booking)}`}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_CONFIRM)}
        confirmLoading={cancelling}
        onConfirm={() => {
          setCancelSheetOpen(false);
          onCancel();
        }}
        onClose={() => setCancelSheetOpen(false)}
      />

      <ConfirmBottomSheet
        open={cancelQuoteSheetOpen}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_CONFIRM)}
        description={`${conversation.heavyDutyMachineryTitle} · ${dateTimeRangeLabel(booking)}`}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_BUTTON)}
        confirmLoading={cancellingQuote}
        onConfirm={() => {
          setCancelQuoteSheetOpen(false);
          onCancelQuote?.();
        }}
        onClose={() => setCancelQuoteSheetOpen(false)}
      />
        </>
      )}

      {reportMessageId && (
        <ReportModal open targetType="Message" targetId={reportMessageId} onClose={() => setReportMessageId(undefined)} />
      )}

      <div style={{ flexShrink: 0, padding: '12px 16px' }}>
        {/* Booking status gates *writing* (see ConversationsController.
            SendMessage, CHAT_ENABLED_STATUSES) -- a disabled status can
            still be read (the thread above always loads), but this input is
            disabled with an explicit reason instead of sitting there
            looking usable and failing silently or with a raw backend
            string once tapped. */}
        {!isChatEnabled && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_NOT_AVAILABLE)}
          </Text>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <TextArea
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={(e) => {
              // Plain Enter sends (matching the old single-line input); Shift+Enter
              // still inserts a real newline, same as every other chat app.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={!isChatEnabled}
            placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_INPUT_PLACEHOLDER)}
            autoSize={{ minRows: 1, maxRows: 5 }}
            // A single-line <input> can never wrap a long unbroken word (e.g.
            // pasted text with no spaces) -- it just scrolls the overflow off
            // to the right. A growing textarea can, as long as it's also told
            // to break mid-word (overflowWrap) instead of only at whitespace.
            style={{ flex: 1, borderRadius: 20, resize: 'none', overflowWrap: 'anywhere' }}
          />
          <Button
            type="primary"
            shape="circle"
            icon={<IconArrowUp size={18} />}
            loading={sending}
            disabled={!isChatEnabled}
            onClick={handleSend}
            style={{ width: 40, height: 40, minWidth: 40, flexShrink: 0 }}
          />
        </div>
      </div>
    </div>
  );
};

export default MessageThreadPanel;
